import React from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Home,
  Search,
  Person,
  CalendarToday,
  School,
  Message as MessageIcon,
  Notifications as NotificationsIcon,
  HealthAndSafety as HealthAndSafetyIcon,
  Favorite as FavoriteIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Badge } from '@mui/material';
import { useMessages } from '../hooks/useMessages';
import { useNotifications } from '../hooks/useNotifications';

interface NavigationDrawerProps {
  open: boolean;
  onClose: () => void;
}

const NavigationDrawer: React.FC<NavigationDrawerProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = useApp();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { unreadCount } = useMessages();
  const { pendingCount } = useNotifications();

  const handleNavigation = (path: string) => {
    navigate(path);
    onClose(); // Always close the drawer after navigation
  };

  const getMenuItems = () => {
    const items: Array<{
      text: string;
      icon: React.ReactElement;
      path: string;
      description?: string;
    }> = [
      {
        text: 'Dashboard',
        icon: <Home />,
        path: '/dashboard',
        description: 'Home',
      },
    ];

    // Add role-specific search menu
    if (state.user?.role === 'PATIENT') {
      items.push({
        text: 'Search Caretakers',
        icon: <Search />,
        path: '/search',
        description: 'Find qualified caretakers',
      });
      // FEAT-057 — Save / Favorite Caregivers (MVP-18.29). Careseeker-facing
      // list of saved caregivers.
      items.push({
        text: 'Saved Caregivers',
        icon: <FavoriteIcon />,
        path: '/favorites',
        description: 'Caregivers you saved',
      });
    } else if (state.user?.role === 'CAREGIVER') {
      items.push({
        text: 'Search Careseekers',
        icon: <Search />,
        path: '/search-patients',
        description: 'Find careseekers who need care',
      });
    }

    items.push({
      text: 'My Appointments',
      icon: <CalendarToday />,
      path: '/appointments',
      description: state.user?.role === 'PATIENT' 
        ? 'View your scheduled appointments'
        : 'View your upcoming appointments',
    });

    // FEAT-056 — Customizable Care Reports (MVP-18.34). Report builder is
    // available to both careseekers and caregivers.
    items.push({
      text: 'Care Reports',
      icon: <AssessmentIcon />,
      path: '/care-reports',
      description: 'Build & export a visit report',
    });

    // Add Messages menu item for all authenticated users
    items.push({
      text: 'Messages',
      icon: unreadCount > 0 ? (
        <Badge badgeContent={unreadCount} color="error">
          <MessageIcon />
        </Badge>
      ) : (
        <MessageIcon />
      ),
      path: '/messages',
      description: 'Send and receive messages',
    });

    items.push({
      text: 'Notifications',
      icon: pendingCount > 0 ? (
        <Badge badgeContent={pendingCount} color="error">
          <NotificationsIcon />
        </Badge>
      ) : (
        <NotificationsIcon />
      ),
      path: '/notifications',
      description: 'Booking and message updates',
    });

    // Add Training menu item for caregivers
    if (state.user?.role === 'CAREGIVER' || state.user?.role === 'ADMIN') {
      items.push({
        text: 'My Training',
        icon: <School />,
        path: '/training',
        description: 'Training courses & certificates',
      });
    }

    // FEAT-044 (MVP-8.10a, MVP-18.31): calm Safety entry visible on every
    // authenticated screen. Copy stays neutral ("Safety", not "Emergency")
    // per work-item tone requirement.
    items.push({
      text: 'Safety',
      icon: <HealthAndSafetyIcon />,
      path: '/safety',
      description: 'Local non-emergency contacts',
    });

    // Add profile at the end
    items.push({
      text: state.user?.role === 'CAREGIVER' ? 'My Profile' : 'Profile',
      icon: <Person />,
      path: state.user?.role === 'CAREGIVER' ? `/caretaker/${state.user.id}` : '/profile',
    });

    return items;
  };

  const menuItems = getMenuItems();

  return (
    <Drawer
      variant="temporary"
      anchor="left"
      open={open}
      onClose={onClose}
      ModalProps={{
        keepMounted: true, // Better open performance on mobile
      }}
      sx={{
        '& .MuiDrawer-paper': {
          width: 280,
          boxSizing: 'border-box',
        },
      }}
    >
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
          Careva
        </Typography>
        {state.user && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {state.user.displayName || state.user.email}
          </Typography>
        )}
      </Box>
      
      <Divider />
      
      <List>
        {menuItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <ListItem key={item.path} disablePadding>
              <ListItemButton
                onClick={() => handleNavigation(item.path)}
                selected={isActive}
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: theme.palette.primary.main + '20',
                    '&:hover': {
                      backgroundColor: theme.palette.primary.main + '30',
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: isActive ? theme.palette.primary.main : 'inherit',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.text}
                  secondary={item.description}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Drawer>
  );
};

export default NavigationDrawer;
